import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { BanknotesIcon, ChevronDownIcon, ExclamationTriangleIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { saldosDeCliente, facturasPorPagar, DIAS_BURO } from '../utils/saldosCliente';
import { registrarCreditoUso, cancelarCreditoUso } from '../api/comisiones';
import { useUser } from './UserContext';

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n) || 0);

const fmtFecha = (ms) =>
  ms ? new Date(Number(ms)).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

// Same live money-input helpers the línea de afianzamiento editor uses.
function formatInputWithCommas(raw) {
  const digits = raw.replace(/[^0-9]/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('en-US');
}

function parseFormattedNumber(formatted) {
  return parseFloat(String(formatted).replace(/[^0-9]/g, '')) || 0;
}

/**
 * SaldosClienteCard — what the cliente owes its afianzadoras and what they hold
 * for the cliente, at a glance.
 *
 * Credit and debt are shown apart and never netted into one number: they sit
 * with specific afianzadoras and a credit at one does not settle a debt at
 * another. The breakdown makes that explicit.
 */
export function SaldosClienteCard({ contracts, clientId, onFianzaClick }) {
  // Applying a credit is a broker action; portal clients only see their saldos.
  const { isClient, userData } = useUser();
  const userOrg = userData?.org;
  const [open, setOpen] = useState(false);
  const [creditos, setCreditos] = useState([]);
  const [usandoCredito, setUsandoCredito] = useState(null); // creditoId with the uso form open
  const [usoMonto, setUsoMonto] = useState('');
  const [usoNota, setUsoNota] = useState('');
  const [usoSaving, setUsoSaving] = useState(false);
  const [usosAbiertos, setUsosAbiertos] = useState(null); // creditoId with usos expanded
  const saldosFacturas = useMemo(() => saldosDeCliente(contracts), [contracts]);
  const pendientes = useMemo(() => facturasPorPagar(contracts), [contracts]);

  // Cliente credits (saldo a favor from anulaciones of paid fianzas) — written
  // only by backend-nyx; this is a read of a small per-cliente subcollection.
  useEffect(() => {
    if (!clientId || !userOrg) return undefined;
    // The org filter is what lets the rules prove the read (the creditos rule
    // requires resource.data.org == the caller's org for a list) — same
    // pattern as the obligados/bienes subcollection queries.
    return onSnapshot(
      query(collection(db, 'clientes', clientId, 'creditos'), where('org', '==', userOrg)),
      (snap) => setCreditos(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error('creditos listener error', e)
    );
  }, [clientId, userOrg]);

  // Credits fold into the same per-afianzadora view as the factura-derived
  // saldos: one "a favor" number per carrier, never netted across carriers.
  const saldos = useMemo(() => {
    const disponibleTotal = creditos.reduce((s, c) => s + (Number(c.disponible) || 0), 0);
    const byAf = new Map(saldosFacturas.porAfianzadora.map((a) => [a.afianzadora, { ...a }]));
    creditos.forEach((c) => {
      const disp = Number(c.disponible) || 0;
      if (!disp) return;
      const key = c.afianzadora || 'Sin afianzadora';
      const entry = byAf.get(key) || { afianzadora: key, saldoEnContra: 0, saldoAFavor: 0, neto: 0, facturasPendientes: 0 };
      entry.saldoAFavor += disp;
      entry.neto = entry.saldoAFavor - entry.saldoEnContra;
      byAf.set(key, entry);
    });
    return {
      ...saldosFacturas,
      saldoAFavor: saldosFacturas.saldoAFavor + disponibleTotal,
      porAfianzadora: Array.from(byAf.values()).sort((a, b) => b.saldoEnContra - a.saldoEnContra),
    };
  }, [saldosFacturas, creditos]);

  // Live credits plus fully-used ones with history — a spent credit must not
  // vanish, its usos ARE the record of where the cliente's money went.
  const creditosVivos = useMemo(
    () => creditos
      .filter((c) => (Number(c.disponible) || 0) > 0 || (c.usos || []).length > 0)
      .sort((a, b) => (Number(b.fecha) || 0) - (Number(a.fecha) || 0)),
    [creditos]
  );

  const cancelarUso = async (credito, uso, index) => {
    setUsoSaving(true);
    try {
      await cancelarCreditoUso({
        clienteId: clientId,
        creditoId: credito.id,
        usoId: uso.id || null,
        usoIndex: uso.id ? null : index,
      });
    } catch (e) {
      console.error('cancelar uso failed', e);
      window.alert(e.message || 'No se pudo cancelar el uso.');
    } finally {
      setUsoSaving(false);
    }
  };

  // montoExacto bypasses the comma field ("Usar todo" applies the precise
  // disponible, centavos included, instead of the integer-formatted input).
  const guardarUso = async (credito, montoExacto = null) => {
    const monto = montoExacto ?? parseFormattedNumber(usoMonto);
    if (!monto || monto <= 0) {
      window.alert('Indica el monto a utilizar.');
      return;
    }
    if (monto > (Number(credito.disponible) || 0) + 0.01) {
      window.alert('El monto excede el saldo disponible.');
      return;
    }
    setUsoSaving(true);
    try {
      await registrarCreditoUso({
        clienteId: clientId,
        creditoId: credito.id,
        monto,
        nota: usoNota.trim() || null,
      });
      setUsandoCredito(null);
      setUsoMonto('');
      setUsoNota('');
    } catch (e) {
      console.error('credito uso failed', e);
      window.alert(e.message || 'No se pudo registrar el uso del crédito.');
    } finally {
      setUsoSaving(false);
    }
  };

  // Resolve a pending row back to its fianza object so the parent can open
  // FianzaView. contratoId is stamped in case the fianza predates that field.
  const openFianza = (fianzaId) => {
    if (!onFianzaClick) return;
    for (const contract of contracts || []) {
      const fianza = (contract?.fianzas || []).find((f) => f.id === fianzaId);
      if (fianza) {
        onFianzaClick({ ...fianza, contratoId: fianza.contratoId || contract.id });
        return;
      }
    }
  };

  // Nothing billed and nothing owed — do not take up room.
  if (!saldos.saldoEnContra && !saldos.saldoAFavor) return null;

  const desglose = saldos.porAfianzadora;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
          <BanknotesIcon className="w-4 h-4 text-azul-surelia" />
          Saldos con afianzadoras
        </h3>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div
          className={`rounded-xl border p-3 ${
            saldos.saldoEnContra > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-zinc-50'
          }`}
          title="Primas facturadas que el cliente aún no paga a la afianzadora. Hasta que se paguen, la comisión tampoco se puede cobrar."
        >
          <div className={`text-[10px] uppercase tracking-wide ${saldos.saldoEnContra > 0 ? 'text-red-600' : 'text-gray-500'}`}>
            Por pagar a la afianzadora
          </div>
          <div className={`text-lg font-bold ${saldos.saldoEnContra > 0 ? 'text-red-800' : 'text-gray-400'}`}>
            {fmtMXN(saldos.saldoEnContra)}
          </div>
          {saldos.facturasPendientes > 0 && (
            <div className="text-[11px] text-red-700 mt-0.5">
              {saldos.facturasPendientes} factura{saldos.facturasPendientes === 1 ? '' : 's'} pendiente
              {saldos.facturasPendientes === 1 ? '' : 's'}
            </div>
          )}
        </div>

        <div
          className={`rounded-xl border p-3 ${
            saldos.saldoAFavor > 0 ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-zinc-50'
          }`}
          title="Dinero que el cliente ya pagó y que una reexpedición posterior dejó sin cubrir. La afianzadora lo conserva como saldo a favor y se aplica a la siguiente prima."
        >
          <div className={`text-[10px] uppercase tracking-wide ${saldos.saldoAFavor > 0 ? 'text-emerald-600' : 'text-gray-500'}`}>
            Saldo a favor del cliente
          </div>
          <div className={`text-lg font-bold ${saldos.saldoAFavor > 0 ? 'text-emerald-800' : 'text-gray-400'}`}>
            {fmtMXN(saldos.saldoAFavor)}
          </div>
        </div>
      </div>

      {/* Créditos vivos: saldo a favor por anulaciones de fianzas pagadas. */}
      {creditosVivos.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">
            Créditos con afianzadoras
          </div>
          <ul className="space-y-1.5">
            {creditosVivos.map((c) => {
              const agotado = (Number(c.disponible) || 0) <= 0;
              const usos = c.usos || [];
              return (
              <li
                key={c.id}
                className={`rounded-lg border px-3 py-2 ${
                  agotado ? 'border-gray-200 bg-zinc-50' : 'border-emerald-200 bg-emerald-50/50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <span className={`text-xs font-semibold ${agotado ? 'text-gray-500' : 'text-emerald-800'}`}>
                      {fmtMXN(c.disponible)}
                    </span>
                    {Number(c.disponible) !== Number(c.monto) && (
                      <span className="ml-1 text-[11px] text-gray-500">de {fmtMXN(c.monto)}</span>
                    )}
                    <span className="ml-2 text-xs text-gray-700 truncate" title={c.afianzadora}>
                      {c.afianzadora}
                    </span>
                    <span className="ml-2 text-[11px] text-gray-400">
                      Anulación #{c.origen?.numeroFianza || 'sin número'} · {fmtFecha(c.fecha)}
                    </span>
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    {usos.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setUsosAbiertos((prev) => (prev === c.id ? null : c.id))}
                        className="text-[11px] text-gray-500 inline-flex items-center gap-1 hover:underline"
                      >
                        {usos.length} uso{usos.length === 1 ? '' : 's'}
                        <ChevronDownIcon className={`w-3 h-3 transition-transform ${usosAbiertos === c.id ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                    {!isClient && !agotado && usandoCredito !== c.id && (
                      <button
                        type="button"
                        onClick={() => { setUsandoCredito(c.id); setUsoMonto(''); setUsoNota(''); }}
                        className="text-[11px] font-medium text-azul-surelia hover:underline"
                      >
                        Usar saldo
                      </button>
                    )}
                  </div>
                </div>
                {usosAbiertos === c.id && usos.length > 0 && (
                  <ul className="mt-2 border-t border-gray-200 pt-1.5 space-y-1">
                    {usos
                      .map((u, i) => ({ ...u, _idx: i }))
                      .sort((a, b) => (b.fecha || 0) - (a.fecha || 0))
                      .map((u) => (
                      <li key={u.id || u._idx} className="flex items-baseline justify-between gap-2 text-[11px]">
                        <span className={`truncate ${u.cancelado ? 'text-gray-400 line-through' : 'text-gray-500'}`}>
                          {fmtFecha(u.fecha)}
                          {u.nota && <span className="ml-1.5 text-gray-400">{u.nota}</span>}
                        </span>
                        <span className="shrink-0 inline-flex items-center gap-1.5">
                          <span className={`font-medium tabular-nums ${u.cancelado ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                            -{fmtMXN(u.monto)}
                          </span>
                          {!isClient && !u.cancelado && (
                            <button
                              type="button"
                              onClick={() => cancelarUso(c, u, u._idx)}
                              disabled={usoSaving}
                              title="Cancelar este uso y devolver el monto al saldo"
                              className="text-gray-300 hover:text-red-500"
                            >
                              <XMarkIcon className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {usandoCredito === c.id && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center rounded border border-azul-surelia/40 bg-white px-1.5 py-0.5 focus-within:ring-1 focus-within:ring-azul-surelia">
                      <span className="text-zinc-400 mr-1">$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={usoMonto}
                        onChange={(e) => setUsoMonto(formatInputWithCommas(e.target.value))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') guardarUso(c);
                          if (e.key === 'Escape') setUsandoCredito(null);
                        }}
                        disabled={usoSaving}
                        autoFocus
                        placeholder="Monto"
                        className="w-24 text-right text-xs outline-none bg-transparent"
                      />
                    </span>
                    <button
                      type="button"
                      onClick={() => guardarUso(c, Number(c.disponible) || 0)}
                      disabled={usoSaving}
                      className="text-[11px] text-azul-surelia hover:underline shrink-0"
                    >
                      Usar todo
                    </button>
                    <input
                      type="text"
                      value={usoNota}
                      onChange={(e) => setUsoNota(e.target.value)}
                      disabled={usoSaving}
                      placeholder="Nota (fianza, referencia…)"
                      className="flex-1 min-w-[140px] rounded border border-gray-300 px-2 py-1 text-xs outline-none focus:border-azul-surelia"
                    />
                    <button type="button" onClick={() => guardarUso(c)} disabled={usoSaving}
                      className="text-emerald-600 hover:text-emerald-800">
                      <CheckIcon className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => setUsandoCredito(null)} disabled={usoSaving}
                      className="text-gray-400 hover:text-red-500">
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Aging of unpaid facturas. At DIAS_BURO days without payment the
          afianzadora can report the cliente to buró de crédito, so this list is
          always visible while anything is pending. */}
      {pendientes.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">
            Facturas por pagar
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                  <th className="py-1.5 pr-3">Fianza</th>
                  <th className="py-1.5 px-3">Afianzadora</th>
                  <th className="py-1.5 px-3 text-right">Prima</th>
                  <th className="py-1.5 px-3 text-right">Emisión</th>
                  <th className="py-1.5 pl-3 text-right">Días sin pago</th>
                </tr>
              </thead>
              <tbody>
                {pendientes.map((p) => (
                  <tr key={`${p.fianzaId}_${p.facturaId || 'sf'}`} className="border-b border-gray-100 last:border-0">
                    <td className="py-1.5 pr-3">
                      {onFianzaClick ? (
                        <button
                          type="button"
                          onClick={() => openFianza(p.fianzaId)}
                          className="font-medium text-azul-surelia hover:underline"
                        >
                          {p.numeroFianza}
                        </button>
                      ) : (
                        <span className="text-gray-800 font-medium">{p.numeroFianza}</span>
                      )}
                    </td>
                    <td className="py-1.5 px-3 text-gray-600 max-w-[160px] truncate" title={p.afianzadora}>
                      {p.afianzadora}
                    </td>
                    <td className="py-1.5 px-3 text-right text-red-700">{fmtMXN(p.prima)}</td>
                    <td className="py-1.5 px-3 text-right text-gray-600">
                      {p.fechaMs
                        ? new Date(p.fechaMs).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                    <td className="py-1.5 pl-3 text-right">
                      {p.dias === null ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <span
                          className={`inline-flex items-center gap-1 font-semibold px-1.5 py-0.5 rounded ${
                            p.enRiesgoBuro
                              ? 'bg-red-100 text-red-800'
                              : p.enAviso
                                ? 'bg-amber-100 text-amber-800'
                                : 'text-gray-700'
                          }`}
                          title={
                            p.enRiesgoBuro
                              ? `Más de ${DIAS_BURO} días sin pago: la afianzadora puede reportar al cliente en buró de crédito.`
                              : p.enAviso
                                ? `Se acerca al límite de ${DIAS_BURO} días sin pago.`
                                : undefined
                          }
                        >
                          {p.enRiesgoBuro && <ExclamationTriangleIcon className="w-3 h-3" />}
                          {p.dias} días
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* The desglose toggle sits right above where the table expands. */}
      {desglose.length > 1 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-[11px] text-azul-surelia inline-flex items-center gap-1 hover:underline"
          >
            {open ? 'Ocultar afianzadoras' : `Ver ${desglose.length} afianzadoras`}
            <ChevronDownIcon className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
      )}

      {open && desglose.length > 1 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="py-1.5 pr-3">Afianzadora</th>
                <th className="py-1.5 px-3 text-right">Por pagar</th>
                <th className="py-1.5 pl-3 text-right">A favor</th>
              </tr>
            </thead>
            <tbody>
              {desglose.map((a) => (
                <tr key={a.afianzadora} className="border-b border-gray-100 last:border-0">
                  <td className="py-1.5 pr-3 text-gray-800">{a.afianzadora}</td>
                  <td className="py-1.5 px-3 text-right text-red-700">
                    {a.saldoEnContra ? fmtMXN(a.saldoEnContra) : '—'}
                  </td>
                  <td className="py-1.5 pl-3 text-right text-emerald-700">
                    {a.saldoAFavor ? fmtMXN(a.saldoAFavor) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SaldosClienteCard;
